import React, { useState } from 'react';

const HelpLegend = () => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleLegend = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="fixed top-4 right-4 z-50">
      {/* Help Button */}
      <button
        onClick={toggleLegend}
        className="bg-white/80 backdrop-blur-sm border border-gray-300 rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-white/90 transition-all duration-200 text-gray-700 hover:text-gray-900"
        title="Show help"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {/* Help Panel */}
      {isOpen && (
        <div className="absolute top-14 right-0 bg-white/95 backdrop-blur-sm border border-gray-300 rounded-xl shadow-xl p-6 w-96 max-w-md max-h-[80vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-800">About Mementos</h3>
            <button
              onClick={toggleLegend}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Main Content */}
          <div className="space-y-4 mb-8 text-sm">
            <p className="leading-relaxed text-gray-700">
              Every conversation creates a trail of meaning. We call these pieces of the trail <strong className="text-gray-900">Mementos</strong> — they form as you talk and interact with your 
              assistant. Each Memento captures context, intent, and outcomes. Together they link into a living 
              map of your work, ideas, and preferences.
            </p>
            <p className="leading-relaxed text-gray-700">
              This map is powerful. It shapes how well your AI companion understands you and how quickly it can help.
            </p>
            <p className="leading-relaxed text-gray-700">
              <strong className="text-gray-900">OpenInfer</strong> brings you that control. You get a high quality, local-first AI companion while you stay in charge 
              of your information. Critical Mementos stay on your device. You can choose what to share, you can delete anything, 
              and you can modify what is kept.
            </p>
          </div>

          {/* How to Use Section */}
          <div className="pt-6 border-t border-gray-200">
            <h4 className="text-base font-semibold text-gray-800 mb-4">How to use this site</h4>
            
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Click Nodes</div>
                  <div className="text-gray-600">Click any node to view and edit its details</div>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Search</div>
                  <div className="text-gray-600">Use the search box to filter mementos by content</div>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Zoom & Pan</div>
                  <div className="text-gray-600">Scroll to zoom, drag to pan around the graph</div>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Node Sizes</div>
                  <div className="text-gray-600">Larger nodes represent the number of mementos connected</div>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Private Nodes</div>
                  <div className="text-gray-600">Lock icons indicate private or sensitive content</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpLegend;
